import { useCallback, useRef } from 'react';

import { PROJECT_TYPE } from 'src/data/project';
import { GENERATION_TOOL } from 'src/data/generationTool';
import * as events from 'src/lib/events';
import { useCreateProject, useTrackingEvent } from 'src/services';
import {
  GENERATION_TOOL_POPUP,
  useBrandVoicePopup,
  useDeleteBrandVoice,
  useGenerationToolPopup,
} from 'src/services/popup';
import { SIDEBAR_TAB_TYPE } from 'src/views/GenerationToolPopup/const';
import { useSidebarEvents } from '../../service';
import { useMainActions } from '../../store';
import { SIDEBAR_ACTION } from '../../const';

export const useHandlers = ({ openNewProjectMenu, closeNewProjectMenu }) => {
  const { trackNavigationItemClicked } = useSidebarEvents();
  const mainActions = useMainActions();
  const openGenerationToolPopup = useGenerationToolPopup();
  const createProject = useCreateProject();
  const trackEvent = useTrackingEvent();
  const openBrandVoicePopup = useBrandVoicePopup();
  const deleteBrandVoice = useDeleteBrandVoice();

  const refHandleGenerationToolPopupSubmit = useRef();
  refHandleGenerationToolPopupSubmit.current = ({
    tool,
    freestyleToolId,
    customMultiPromptId,
    customMultiPromptGroup,
  }) => {
    if (tool === GENERATION_TOOL.blogDescription) {
      createProject(PROJECT_TYPE.blogPostBuilder);
      return;
    }

    createProject(PROJECT_TYPE.document, {
      generationTool: tool,
      freestyleGenerationToolId: freestyleToolId,
      customMultiPromptId,
      customMultiPromptGroup,
    });
  };

  const handleNewProjectClick = useCallback(
    (event) => {
      openNewProjectMenu(event.currentTarget);
      trackEvent(events.DASHBOARD.clickedCreateProjectMenuButton, {
        page: events.EVENT_PAGE.homePage,
      });
    },
    [openNewProjectMenu, trackEvent]
  );

  const handleToggleMenu = useCallback(
    (menuItem) => {
      mainActions.toggleMenuItem(menuItem?.id);
    },
    [mainActions]
  );

  const handleToggleCollapse = useCallback(
    (collapseItem) => {
      mainActions.toggleCollapseItem(collapseItem?.id);
    },
    [mainActions]
  );

  const handleLinkClick = useCallback(
    (linkItem) => {
      trackNavigationItemClicked(linkItem.label);

      if (!linkItem.isChildItem && !linkItem.disableNavigation) {
        mainActions.collapseAll();
      }
      if (linkItem.popupKey === GENERATION_TOOL_POPUP) {
        openGenerationToolPopup({
          selectedTabType: SIDEBAR_TAB_TYPE.mostPopular,
          onSubmit: refHandleGenerationToolPopupSubmit.current,
          withBlog: true,
        });
      }
    },
    [mainActions, trackNavigationItemClicked, openGenerationToolPopup]
  );

  const handleCreateDataDrivenProject = useCallback(() => {
    trackEvent(events.DASHBOARD.clickedCreateDataDrivenButton, {
      page: events.EVENT_PAGE.homePage,
    });

    openGenerationToolPopup({
      selectedTabType: SIDEBAR_TAB_TYPE.mostPopular,
      onSubmit: refHandleGenerationToolPopupSubmit.current,
      withBlog: false,
    });

    closeNewProjectMenu();
  }, [closeNewProjectMenu, openGenerationToolPopup, trackEvent]);

  const handleCreateBlogProject = useCallback(() => {
    trackEvent(events.DASHBOARD.clickedCreateBlogButton, {
      page: events.EVENT_PAGE.homePage,
    });

    createProject(PROJECT_TYPE.blogPostBuilder, {
      generationTool: GENERATION_TOOL.blogDescription,
    });

    closeNewProjectMenu();
  }, [closeNewProjectMenu, createProject, trackEvent]);

  const handleSidebarAction = useCallback(
    (type, data) => {
      switch (type) {
        case SIDEBAR_ACTION.createBrandVoice: {
          openBrandVoicePopup();
          break;
        }

        case SIDEBAR_ACTION.editBrandVoice: {
          openBrandVoicePopup(data);
          break;
        }

        case SIDEBAR_ACTION.deleteBrandVoice: {
          deleteBrandVoice(data.id, data.name);
          break;
        }

        default: {
          // do nothing
        }
      }
    },
    [openBrandVoicePopup, deleteBrandVoice]
  );

  return {
    handleNewProjectClick,
    handleToggleMenu,
    handleToggleCollapse,
    handleLinkClick,
    handleCreateDataDrivenProject,
    handleCreateBlogProject,
    handleSidebarAction,
  };
};
